import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchStickerPack, fetchStickerPacks, updateStickerPack, createStickerPack } from "../../actions/sticker_maker/stickerPacksActions";
import {createSticker, destroySticker, fetchSticker, fetchStickers} from "../../actions/sticker_maker/stickersActions";
import { fetchReport, fetchReports } from "../../actions/sticker_maker/reportsActions";

export interface StickerPackInterface {
  url: string;

  id: number;
  target: number;
  user_id?: number;
  user?: number;
  name: string;
  keywords: string;
  search_text: string;
  image_url: string;

  category_ids: number[];

  likes_count: number;
  popular_count: number;
  stickers_count: number;
  active_stickers_count: number;

  pro: number;
  telegram_url: string;

  active: boolean;
  status: number;
  published: boolean;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: StickerPackInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<StickerPackInterface>();

const slice = createSlice({
  name: 'stickerPacks',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchStickerPacks.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchStickerPacks.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.sticker_packs;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchStickerPacks.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchStickerPack.fulfilled, (state, action) => {
        const stickerPack = Object.values(action.payload.sticker_pack)[0];
        state.entities[stickerPack.id] = stickerPack;
      })

      // #create
      .addCase(createStickerPack.fulfilled, (state, action) => {
        const stickerPack = Object.values(action.payload.sticker_pack)[0];
        state.entities[stickerPack.id] = stickerPack;
      })
      .addCase(createStickerPack.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateStickerPack.fulfilled, (state, action) => {
        const stickerPack = Object.values(action.payload.sticker_pack)[0];
        state.entities[stickerPack.id] = stickerPack;
      })
      .addCase(updateStickerPack.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #stickers
      .addCase(createSticker.fulfilled, (state, action) => {
        const sticker = Object.values(action.payload.sticker)[0];
        const stickerPack = state.entities[sticker.sticker_pack_id];
        if (stickerPack) {
          stickerPack.stickers_count += 1;
        }
      })
      .addCase(destroySticker.fulfilled, (state, action) => {
        const stickerPack = state.entities[action.payload.stickerPackId];
        if (stickerPack) {
          stickerPack.stickers_count -= 1;
        }
      })

      // #reports
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.sticker_packs };
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.sticker_pack };
      })

      // #stickers
      .addCase(fetchStickers.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.sticker_packs };
      })
      .addCase(fetchSticker.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.sticker_pack };
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectStickerPackById,
  selectIds: selectStickerPackIds,
  selectEntities: selectStickerPackEntities,
  selectAll: selectAllStickerPacks,
  selectTotal: selectTotalStickerPacks
} = adaptor.getSelectors<RootState>(state => state.stickerMaker.stickerPacks);
